import { useState } from 'react'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { selectProjectById, selectProjectMemberIds, selectProjectInvite } from '@/store/projects/projectsSelectors'
import {
    addMemberSaga,
    removeMemberSaga,
    inviteToProjectSaga,
    setProjectInviteError,
} from '@/store/projects/projectsSlice'
import { AGENT_USER_ID } from '@/const/constants'
import { getMemberDisplayName } from '@/utils/memberDisplayName'
import Modal from '@/components/modals/Modal/Modal'
import Avatar from '@/components/ui/Avatar/Avatar'
import Badge from '@/components/ui/Badge/Badge'
import Toggle from '@/components/ui/Toggle/Toggle'
import Button from '@/components/ui/Button/Button'
import Input from '@/components/ui/Input/Input'
import IconButton from '@/components/ui/IconButton/IconButton'
import FormDivider from '@/components/ui/Form/components/FormDivider/FormDivider'
import FormTitle from '@/components/ui/Form/components/FormTitle/FormTitle'
import IconClose from '@/assets/icons/icon-close.svg?react'
import { TextH2, TextMidd, TextNormBold } from '@/components/ui/Text'
import css from './ProjectMembersModal.module.css'

interface Props {
    projectId: string
    onClose: () => void
}

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const ProjectMembersModal = ({ projectId, onClose }: Props) => {
    const dispatch = useAppDispatch()
    const { members, workspace } = useWorkspace()
    const project = useAppSelector(state => selectProjectById(state, projectId))
    const projectMemberIds = useAppSelector(state => selectProjectMemberIds(state, projectId))
    const invite = useAppSelector(selectProjectInvite)

    const currentUserId = useAppSelector(state => state.auth?.id)
    const workspaceId = workspace?.id
    const ownerId = project?.ownerId
    // Only the workspace owner can invite new people (matches Workspace Settings + the
    // inviteMember function's own check).
    const isCurrentUserOwner = members.find(m => m.userId === currentUserId)?.role === 'owner'

    const [inviteOpen, setInviteOpen] = useState(false)
    const [inviteEmail, setInviteEmail] = useState('')

    const activeMembers = members.filter(m => m.status !== 'suspended')
    const humanMembers = activeMembers.filter(m => m.userId !== AGENT_USER_ID)
    const agentMember = activeMembers.find(m => m.userId === AGENT_USER_ID)

    const toggleMember = (userId: string, enabled: boolean) => {
        if (!workspaceId) return
        if (enabled) {
            dispatch(addMemberSaga({ projectId, workspaceId, userId }))
        } else {
            dispatch(removeMemberSaga({ projectId, userId }))
        }
    }

    const handleInvite = () => {
        dispatch(setProjectInviteError(null))
        const email = inviteEmail.trim()
        if (!isValidEmail(email)) {
            dispatch(setProjectInviteError('Please enter a valid email'))
            return
        }
        if (!workspaceId) return
        dispatch(inviteToProjectSaga({ projectId, workspaceId, email, clearInput: () => setInviteEmail('') }))
    }

    const handleInviteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleInvite()
        }
    }

    return (
        <Modal onClose={onClose} maxWidth={460}>
            <div className={css.header}>
                <TextH2>Project members</TextH2>
                <IconButton icon={<IconClose />} onClick={onClose} />
            </div>
            <FormDivider />

            <div className={css.list}>
                {humanMembers.map(member => {
                    const isOwner = member.userId === ownerId
                    const inProject = projectMemberIds.has(member.userId)
                    return (
                        <div key={member.userId} className={css.row}>
                            <Avatar
                                size="medium2"
                                title={member.fullName || member.username}
                                color={member.color}
                                avatar={member.avatarImage}
                            />
                            <div className={css.info}>
                                <div className={css.name_row}>
                                    <TextNormBold>{member.fullName || member.username}</TextNormBold>
                                    {member.status === 'pending' && <Badge>Pending</Badge>}
                                </div>
                                <TextMidd className={css.sub}>{member.username} • {member.email}</TextMidd>
                            </div>
                            {!isOwner && (
                                <Toggle checked={inProject} onChange={(v) => toggleMember(member.userId, v)} />
                            )}
                        </div>
                    )
                })}
            </div>

            {isCurrentUserOwner && (
                <>
                    <div className={css.invite_button}>
                        <Button type="light" size="medium" title="Invite people" onClick={() => setInviteOpen(o => !o)} />
                    </div>
                    {inviteOpen && (
                        <div className={css.invite}>
                            <Input
                                value={inviteEmail}
                                onChange={(e) => {
                                    setInviteEmail(e.target.value)
                                    dispatch(setProjectInviteError(null))
                                }}
                                onKeyDown={handleInviteKeyDown}
                                placeholder="Invite by email"
                                disabled={invite.loading}
                            />
                            {invite.error && <div className={css.error}>{invite.error}</div>}
                        </div>
                    )}
                </>
            )}

            {agentMember && (
                <>
                    <FormDivider />
                    <div className={css.section_title}><FormTitle>AI Agents</FormTitle></div>
                    <div className={css.row}>
                        <Avatar
                            size="medium2"
                            title={getMemberDisplayName(agentMember)}
                            color={agentMember.color}
                            avatar={agentMember.avatarImage}
                        />
                        <div className={css.info}>
                            <TextNormBold>{getMemberDisplayName(agentMember)}</TextNormBold>
                            <TextMidd className={css.sub}>
                                Assign tasks or @mention for quick answers.
                            </TextMidd>
                        </div>
                        <Toggle
                            checked={projectMemberIds.has(agentMember.userId)}
                            onChange={(v) => toggleMember(agentMember.userId, v)}
                        />
                    </div>
                </>
            )}
        </Modal>
    )
}

export default ProjectMembersModal
